import { createEffect, createSignal, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { canForceIssueActions, useIssue } from '../lib/issue-actions';
import type { RepoIssueRow } from '../lib/issues';
import { useMe } from '../lib/session';
import { ActionDrawer } from './ActionDrawer';
import { IssueEscalateForm } from './IssueEscalateForm';
import { IssueHandoffForm } from './IssueHandoffForm';
import { IssueTriageFacts } from './IssueTriageFacts';

// One tracked issue, with the two levers a human may pull on it (arc 101 slice
// 06). Container — resolves `GET /v1/issues/:id`, gates the write forms, and
// hands each re-read back to the list so a forced handoff shows up in the row
// behind the drawer.
//
// The detail read is not redundant with the row that opened it: the drawer
// re-reads after every forced action, so what it shows is what the server
// stored, not what the client hoped. Until that lands the opening row stands in
// (same projection), so the panel never opens blank.

export function IssueDetailDrawer(props: {
  /** The selected row; `null` closes the drawer. */
  issue: RepoIssueRow | null;
  encoded: string;
  token: string | null;
  onClose: () => void;
  /** Fresh server truth for the list to patch its row with. */
  onUpdated: (row: RepoIssueRow) => void;
}): JSX.Element {
  // Snapshot survives the `issue → null` close edge so the exit animation
  // slides out the full panel instead of a blanked one (same as DigestDrawer).
  const [last, setLast] = createSignal<RepoIssueRow | null>(null);
  createEffect(() => {
    if (props.issue) setLast(props.issue);
  });

  const detail = useIssue(() => last()?.id ?? null, props.token);
  const me = useMe(props.token);
  const row = (): RepoIssueRow | null => detail.data ?? last();

  // Every completed read patches the row behind the drawer — a forced handoff
  // must not leave the table claiming the issue was never handed off.
  createEffect(
    on(
      () => detail.data,
      (fresh) => {
        if (fresh) props.onUpdated(fresh);
      },
    ),
  );

  const reread = (): void => void detail.refetch();

  return (
    <ActionDrawer
      open={props.issue !== null}
      title={t('repo_detail.issue_drawer_title', { number: row()?.github_number ?? '' })}
      onClose={props.onClose}
    >
      <Show when={row()}>
        {(issue) => (
          <>
            <IssueTriageFacts issue={issue()} encoded={props.encoded} />

            {/* A stale-but-shown row is fine; silently stale is not. */}
            <Show when={detail.isError}>
              <p class="field__error" role="alert">
                {t('repo_detail.issue_detail_error')}
              </p>
            </Show>

            <Show
              when={canForceIssueActions(me.data?.role)}
              fallback={
                <p class="issue-action__note" role="status">
                  {t('repo_detail.issue_actions_denied')}
                </p>
              }
            >
              <IssueEscalateForm issueId={issue().id} token={props.token} onActed={reread} />
              <IssueHandoffForm issueId={issue().id} token={props.token} onActed={reread} />
            </Show>
          </>
        )}
      </Show>
    </ActionDrawer>
  );
}
